/* ============================================================
   Trasse Families — Reports.

   A month of school runs as one document you can keep, forward, or hand to
   whoever is paying for the fuel.

   Scope, honestly drawn: the run list, the totals, a CSV you can open in any
   spreadsheet, and a print layout that saves as PDF from the browser's own
   dialog. All of it is client-side over the ONE query the household already
   makes, so it costs nothing extra and works the moment a run exists.

   What is NOT here, and why: a report EMAILED on a schedule needs a server —
   a 06:00 cron has no browser open. `premium/reports/` already does exactly
   that for schools (own PDF writer, recurring send), and pointing Families at
   it is a backend job, not a button. Saying "choose a schedule" here and
   silently never sending one would be the worse failure.
   ============================================================ */
import React, { useMemo, useState } from 'react';
import { FAM, DISPLAY_FONT, FAMILY_PAGE_WRAP } from './familyTheme';
import { FamilyRun, minutesLate, runsBetween, totalsFor, useFamilyRuns } from './useFamilyRuns';
import { confirmedSpend, needsParent, useFamilyRefuels } from './useFamilyRefuels';

interface Props { orgCode: string; orgName: string; busId: string }

const Panel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <section style={{
    background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20,
    padding: 'clamp(18px,3vw,26px)', ...style,
  }}>
    {children}
  </section>
);

function startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parseIso(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
function fmtDate(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleDateString('en-KE', { weekday: 'short', day: 'numeric', month: 'short' });
}
function fmtTime(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false });
}
function legLabel(routeType: string): string {
  if (routeType === 'evening') return 'Afternoon';
  if (routeType === 'morning') return 'Morning';
  return routeType || 'Run';
}

/** CSV, quoted properly. A parent forwards this to whoever pays for the fuel,
 *  and a comma inside a field must not silently shift every column. */
function toCsv(rows: string[][]): string {
  return rows
    .map((r) => r.map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(','))
    .join('\n');
}

export default function FamilyReportsPage({ orgCode, orgName, busId }: Props) {
  const { runs, schedule, loading, error } = useFamilyRuns(busId, orgCode);
  const { refuels } = useFamilyRefuels(orgCode);

  const now = new Date();
  const [from, setFrom] = useState(iso(startOfMonth(now)));
  const [to, setTo] = useState(iso(endOfMonth(now)));

  const range = useMemo(() => {
    const f = parseIso(from); const t = parseIso(to);
    if (!f || !t || t < f) return null;
    return { f, t };
  }, [from, to]);

  const scoped: FamilyRun[] = useMemo(
    () => (range ? runsBetween(runs, range.f, range.t) : []),
    [runs, range],
  );
  const totals = useMemo(() => totalsFor(scoped), [scoped]);

  /* Fuel in the same period, counting ONLY declarations the parent agreed to.
     A report handed to whoever pays for the fuel must not include an amount
     the household itself has not accepted. */
  const fuelRows = useMemo(() => {
    if (!range) return [];
    const lo = new Date(range.f); lo.setHours(0, 0, 0, 0);
    const hi = new Date(range.t); hi.setHours(23, 59, 59, 999);
    return refuels.filter((r) => r.declaredAt && r.declaredAt >= lo && r.declaredAt <= hi);
  }, [refuels, range]);
  const fuelSpend = useMemo(() => confirmedSpend(fuelRows), [fuelRows]);
  const fuelAwaiting = useMemo(() => fuelRows.filter(needsParent).length, [fuelRows]);

  const setMonth = (offset: number) => {
    const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    setFrom(iso(startOfMonth(base)));
    setTo(iso(endOfMonth(base)));
  };

  const downloadCsv = () => {
    const header = ['Date', 'Leg', 'Started', 'Finished', 'Minutes', 'Distance km', 'Distance source', 'Driven by', 'Status'];
    const body = scoped.map((r) => [
      r.startedAt ? iso(r.startedAt) : '',
      legLabel(r.routeType),
      fmtTime(r.startedAt),
      fmtTime(r.finishedAt),
      r.durationMinutes != null ? String(r.durationMinutes) : '',
      // An unrecorded distance stays BLANK, never 0 — a spreadsheet will sum a
      // zero and quietly understate the month.
      r.distanceMissing ? '' : String(r.distanceKm ?? ''),
      r.distanceKmSource || '',
      r.driverRole === 'parent' ? 'Parent' : r.driverRole === 'driver' ? 'Driver' : '',
      r.status || '',
    ]);
    const csv = toCsv([
      [`Trasse Families — ${orgName || orgCode}`],
      [`Runs ${from} to ${to}`],
      [],
      header,
      ...body,
      [],
      ['Runs', String(totals.count)],
      ['Distance km', totals.km.toFixed(1)],
      ['Runs with no distance recorded', String(totals.kmMissing)],
      ['Fuel KES (agreed only)', String(Math.round(fuelSpend))],
      ['Refuels still awaiting your confirmation', String(fuelAwaiting)],
    ]);
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trasse-families-${orgCode}-${from}-to-${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <Panel><p style={{ margin: 0, color: FAM.ink2, fontSize: 14.5 }}>Reading your runs…</p></Panel>;
  }
  if (error) {
    return (
      <Panel>
        <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 28, margin: 0 }}>Reports</h2>
        <p style={{ margin: '10px 0 0', fontSize: 14.5, color: FAM.ink2, lineHeight: 1.6 }}>
          We could not read your runs just now. Refresh in a moment.
        </p>
      </Panel>
    );
  }

  const dateInput: React.CSSProperties = {
    font: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 10,
    border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink, minWidth: 0,
  };

  return (
    <div style={FAMILY_PAGE_WRAP}>
      <style>{`
        @media print {
          /* The report is the page; the console around it is not. */
          .pear-shell nav, .pear-side, .pear-topbar, .fam-noprint { display: none !important; }
          .fam-report { border: none !important; padding: 0 !important; }
          body { background: #fff !important; }
        }
      `}</style>

      <Panel>
        <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 30, letterSpacing: '-.01em', margin: 0 }}>
          Reports
        </h2>
        <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
          Every run in a period you choose, with the times and distances as recorded. Take it as a
          spreadsheet, or print it to PDF.
        </p>

        <div className="fam-noprint" style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setMonth(0)} style={{
            padding: '7px 13px', borderRadius: 999, cursor: 'pointer', font: 'inherit', fontSize: 13,
            fontWeight: 700, border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink2,
          }}>This month</button>
          <button type="button" onClick={() => setMonth(-1)} style={{
            padding: '7px 13px', borderRadius: 999, cursor: 'pointer', font: 'inherit', fontSize: 13,
            fontWeight: 700, border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink2,
          }}>Last month</button>
        </div>

        <div className="fam-noprint" style={{
          marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10,
        }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: FAM.ink3 }}>From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={dateInput} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: FAM.ink3 }}>To</span>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={dateInput} />
          </label>
        </div>

        {!range && (
          <p style={{
            margin: '12px 0 0', padding: '10px 13px', borderRadius: 10,
            background: FAM.dangerTint, color: FAM.danger, fontSize: 13.5,
          }}>
            The end date is before the start date.
          </p>
        )}

        <div className="fam-noprint" style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={!scoped.length}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: scoped.length ? FAM.accent : FAM.accentPale,
              color: '#fff', font: 'inherit', fontSize: 14, fontWeight: 700,
              cursor: scoped.length ? 'pointer' : 'not-allowed',
              boxShadow: scoped.length ? '0 4px 13px rgba(0,112,127,.4)' : 'none',
            }}
          >
            Download spreadsheet
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!scoped.length}
            style={{
              padding: '10px 16px', borderRadius: 10, border: `1px solid ${FAM.line}`,
              background: '#fff', color: FAM.ink2, font: 'inherit', fontSize: 13.5, fontWeight: 600,
              cursor: scoped.length ? 'pointer' : 'not-allowed',
            }}
          >
            Print / save as PDF
          </button>
        </div>
      </Panel>

      {/* ── the report itself ─────────────────────────────────────────── */}
      <Panel style={{ overflow: 'hidden' }}>
        <div className="fam-report">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.12em', color: FAM.accent }}>
                TRASSE FAMILIES
              </div>
              <h3 style={{ margin: '6px 0 0', fontSize: 20, fontWeight: 800, color: FAM.ink }}>
                {orgName || orgCode}
              </h3>
              <div style={{ fontSize: 13.5, color: FAM.ink2, marginTop: 2 }}>
                {fmtDate(range?.f ?? null)} — {fmtDate(range?.t ?? null)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {[
                { k: 'Runs', v: String(totals.count) },
                { k: 'Distance', v: `${totals.km.toFixed(1)} km` },
                { k: 'On the road', v: totals.minutes ? `${Math.round(totals.minutes / 60)} h` : '—' },
                { k: 'Fuel', v: fuelSpend ? `KES ${Math.round(fuelSpend).toLocaleString('en-KE')}` : '—' },
              ].map((s) => (
                <div key={s.k}>
                  <div style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em',
                    textTransform: 'uppercase', color: FAM.muted,
                  }}>{s.k}</div>
                  <div style={{
                    fontSize: 20, fontWeight: 800, color: FAM.ink, fontVariantNumeric: 'tabular-nums',
                  }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {totals.kmMissing > 0 && (
            <p style={{
              margin: '14px 0 0', padding: '9px 12px', borderRadius: 9,
              background: FAM.amberTint, color: FAM.amber, fontSize: 13, lineHeight: 1.5,
            }}>
              {totals.kmMissing} run{totals.kmMissing === 1 ? '' : 's'} recorded no distance and
              {totals.kmMissing === 1 ? ' is' : ' are'} left out of the total rather than counted as zero.
            </p>
          )}

          {scoped.length === 0 ? (
            <p style={{ margin: '18px 0 0', fontSize: 14.5, color: FAM.ink2, lineHeight: 1.6 }}>
              No runs in this period.
            </p>
          ) : (
            <div style={{ marginTop: 18, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 620 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: FAM.muted }}>
                    {['Date', 'Leg', 'Started', 'Took', 'Distance', 'Driven by'].map((h) => (
                      <th key={h} style={{
                        padding: '8px 10px', fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em',
                        textTransform: 'uppercase', borderBottom: `1px solid ${FAM.line}`, whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scoped.map((r) => {
                    const late = minutesLate(r, schedule);
                    return (
                      <tr key={r.id}>
                        <td style={{ padding: '9px 10px', borderBottom: `1px solid ${FAM.line}`, whiteSpace: 'nowrap' }}>
                          {fmtDate(r.startedAt)}
                        </td>
                        <td style={{ padding: '9px 10px', borderBottom: `1px solid ${FAM.line}`, whiteSpace: 'nowrap' }}>
                          {legLabel(r.routeType)}
                        </td>
                        <td style={{
                          padding: '9px 10px', borderBottom: `1px solid ${FAM.line}`,
                          whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                        }}>
                          {fmtTime(r.startedAt)}
                          {late != null && late > 10 && (
                            <span style={{ color: FAM.amber, fontWeight: 700, fontSize: 12 }}> +{late}m</span>
                          )}
                        </td>
                        <td style={{
                          padding: '9px 10px', borderBottom: `1px solid ${FAM.line}`,
                          whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                        }}>
                          {r.durationMinutes != null ? `${r.durationMinutes} min` : '—'}
                        </td>
                        <td style={{
                          padding: '9px 10px', borderBottom: `1px solid ${FAM.line}`,
                          whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                          color: r.distanceMissing ? FAM.muted : FAM.ink,
                        }}>
                          {r.distanceMissing ? 'not recorded' : `${(r.distanceKm ?? 0).toFixed(1)} km`}
                        </td>
                        <td style={{ padding: '9px 10px', borderBottom: `1px solid ${FAM.line}`, whiteSpace: 'nowrap' }}>
                          {r.driverRole === 'parent' ? 'You' : r.driverRole === 'driver' ? 'Your driver' : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>

      {/* ── the honest absence ────────────────────────────────────────── */}
      <Panel>
        <span style={{
          display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 10px', borderRadius: 999,
          background: FAM.amberTint, color: FAM.amber, fontSize: 11, fontWeight: 800, letterSpacing: '.06em',
        }}>NOT BUILT YET</span>
        <h3 style={{ margin: '12px 0 0', fontSize: 16.5, fontWeight: 800, color: FAM.ink }}>
          A report that arrives on its own
        </h3>
        <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
          Emailing this to you every month needs a server that wakes up without a browser open. It
          exists for schools already; pointing it at households is work we have not done, so rather
          than offer you a schedule that never fires, this page waits for you to come to it.
        </p>
        <p style={{ margin: '10px 0 0', fontSize: 14, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
          Fuel here counts only what you have agreed to. A declaration still waiting on you, or one
          you have questioned, is deliberately left out of the total rather than quietly included —
          so a report you forward is only ever numbers you stand behind.
        </p>
      </Panel>
    </div>
  );
}
